/* ------------------------------------------------------------------
   SubscriptionSettingsPage.js  (v9.0 – modernized UI, matches dashboard theme)
------------------------------------------------------------------ */
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import BillingHistory from '../subscription/BillingHistory'
import { UserContext } from '../../../context/UserContext'

const API_BASE = '/api'
const baseName = str => (str ? str.split(/[_-]/)[0] : '')

const sectionCard = {
  background  : '#ffffff',
  border      : '1px solid #e5e7eb',
  borderRadius: 12,
  padding     : '22px 24px',
  marginBottom: 20,
  boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
}

export default function SubscriptionSettingsPage () {
  const { user, setUser } = useContext(UserContext)
  const nav               = useNavigate()

  const [plans,      setPlans]    = useState([])
  const [plansReady, setReady]    = useState(false)
  const [invoices,   setInvoices] = useState([])
  const [haveInvoices, setHI]     = useState(false)
  const [portalBusy, setBusy]     = useState(false)
  const [showCan,    setShowCan]  = useState(false)
  const [feedback,   setFB]       = useState('')
  const [canBusy,    setCanBusy]  = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/billing/stripe/plans`, { withCredentials: true })
        setPlans(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error('[SubSettings] Plans fetch failed:', err)
        setPlans([])
      } finally {
        setReady(true)
      }
    })()
  }, [])

  if (user === null || !plansReady) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <span className='spinner-border' role='status' style={{ color: '#2563eb', width: 34, height: 34 }} />
      </div>
    )
  }

  let current = null
  if (user.plan) {
    const target = baseName(String(user.plan).trim().toLowerCase())
    current = plans.find(p => p && p.name && baseName(String(p.name).toLowerCase()) === target) || null
  }

  const planName    = current && current.name
    ? baseName(String(current.name)).replace(/^./, c => c.toUpperCase())
    : (user.plan ? baseName(String(user.plan)).replace(/^./, c => c.toUpperCase()) : 'Free')
  const planPrice   = current ? `$${((current.price || 0) / 100).toFixed(2)} / ${current.interval || 'month'}` : 'No charge'
  const renewalText = user.renewalDate
    ? `Renews on ${new Date(user.renewalDate).toLocaleDateString()}`
    : 'No renewal date'

  const loadInvoices = async () => {
    if (haveInvoices) return
    try {
      const { data } = await axios.get(`${API_BASE}/billing/stripe/invoices`, { withCredentials: true })
      setInvoices(data); setHI(true)
    } catch (err) { console.error('Invoice fetch error:', err) }
  }

  const openPortal = async () => {
    setBusy(true)
    try {
      const { data } = await axios.post(`${API_BASE}/billing/stripe/portal`, {}, { withCredentials: true })
      window.location.href = data.url
    } catch {
      alert('Could not open billing portal.')
      setBusy(false)
    }
  }

  const confirmCancel = async () => {
    if (!feedback.trim()) return
    setCanBusy(true)
    try {
      await axios.post(`/api/billing/stripe/cancel`, { feedback }, { withCredentials: true })
      window.location.reload()
    } catch {
      alert('Could not cancel subscription.')
      setCanBusy(false)
    }
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Subscription</h2>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>Manage your plan, billing, and payment details.</p>
      </div>

      {/* Current plan card */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Plan icon */}
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: current ? '#eff6ff' : '#f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <i className='fas fa-bolt' style={{ fontSize: 20, color: current ? '#2563eb' : '#9ca3af' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <span style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>{planName}</span>
                {current && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#dcfce7', color: '#16a34a' }}>
                    Active
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{planPrice}</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{renewalText}</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <button
              onClick={() => nav('/dashboard/plans')}
              style={{
                padding: '9px 20px', borderRadius: 9, border: 'none',
                background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
              onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}
            >
              <i className='fas fa-arrow-up me-2' style={{ fontSize: 11 }} />
              {user.plan ? 'Change Plan' : 'Upgrade'}
            </button>
            {user.plan && (
              <button
                onClick={() => setShowCan(true)}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: '#9ca3af', textDecoration: 'none', fontWeight: 500,
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
              >
                Cancel plan
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Billing history card */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Payment & Billing History</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Update your payment method or download receipts.</p>
          </div>
          <button
            disabled={portalBusy}
            onClick={openPortal}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 16px', borderRadius: 8,
              border: '1.5px solid #e5e7eb', background: 'transparent',
              color: '#374151', fontSize: 13, fontWeight: 600,
              cursor: portalBusy ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={e => { if (!portalBusy) { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#2563eb' }}}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151' }}
          >
            {portalBusy
              ? <><span className='spinner-border spinner-border-sm' role='status' style={{ width: 13, height: 13 }} /> Loading…</>
              : <><i className='fas fa-external-link-alt' style={{ fontSize: 11 }} /> Manage Billing</>
            }
          </button>
        </div>

        <div onMouseEnter={loadInvoices}>
          <BillingHistory invoices={invoices} />
        </div>
      </div>

      {/* Cancel modal */}
      {showCan && (
        <>
          <div
            onClick={() => canBusy ? null : setShowCan(false)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1050, backdropFilter:'blur(2px)' }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1051, background: '#ffffff', borderRadius: 14,
            width: 420, maxWidth: 'calc(100vw - 32px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '20px 22px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className='fas fa-times' style={{ fontSize: 12, color: '#ef4444' }} />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: '#111827', margin: 0 }}>Cancel Subscription?</h3>
              {!canBusy && (
                <button onClick={() => setShowCan(false)} style={{ marginLeft:'auto', background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:20 }}>×</button>
              )}
            </div>

            {/* Body */}
            <div style={{ padding: '18px 22px' }}>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626', lineHeight: 1.5 }}>
                <strong>Warning:</strong> Cancelling will erase any data on your account and cannot be undone.
              </div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                Reason for cancelling <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                rows={3}
                value={feedback}
                onChange={e => setFB(e.target.value)}
                disabled={canBusy}
                placeholder="Please tell us why you're leaving..."
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8,
                  border: '1.5px solid #e5e7eb', fontSize: 13, color: '#111827',
                  resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                  background: canBusy ? '#f9fafb' : '#ffffff',
                }}
              />
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 22px 18px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowCan(false)}
                disabled={canBusy}
                style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'transparent', color:'#374151', fontSize:13, fontWeight:700, cursor:'pointer' }}
              >
                Keep Subscription
              </button>
              <button
                onClick={confirmCancel}
                disabled={canBusy || !feedback.trim()}
                style={{
                  padding:'9px 18px', borderRadius:8, border:'none',
                  background: (canBusy || !feedback.trim()) ? '#fca5a5' : '#ef4444',
                  color:'#fff', fontSize:13, fontWeight:700,
                  cursor: (canBusy || !feedback.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 7,
                }}
              >
                {canBusy
                  ? <><span className='spinner-border spinner-border-sm' role='status' style={{ width:13, height:13 }} /> Cancelling…</>
                  : 'Cancel Subscription'
                }
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}